/**
 * Cliente HTTP minimalista com:
 *  - injeção automática do JWT salvo em localStorage;
 *  - dispatch de evento 'ictus:auth-expired' em 401 (para o AuthProvider deslogar);
 *  - x-request-id por requisição (corre alinhado ao middleware do worker);
 *  - opção idempotencyKey para mutações.
 */
const TOKEN_KEY = 'ictus.token';
const BASE = import.meta.env.VITE_API_BASE ?? '';

export interface ApiOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  idempotencyKey?: string;
  skipAuth?: boolean;
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId: string | null;
  constructor(status: number, code: string, message: string, details: unknown, requestId: string | null) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

export function getToken(): string | null {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}
export function setToken(token: string | null) {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* storage indisponível */ }
}

function newRequestId(): string {
  return (globalThis.crypto?.randomUUID?.() ?? `req-${Date.now()}-${Math.random().toString(16).slice(2)}`);
}

export async function api<T = unknown>(path: string, opts: ApiOptions = {}): Promise<T> {
  const headers = new Headers(opts.headers);
  headers.set('accept', 'application/json');
  if (opts.body !== undefined) headers.set('content-type', 'application/json');
  if (!opts.skipAuth) {
    const tok = getToken();
    if (tok) headers.set('authorization', `Bearer ${tok}`);
  }
  if (opts.idempotencyKey) headers.set('idempotency-key', opts.idempotencyKey);
  headers.set('x-request-id', newRequestId());

  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers,
    body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
  });

  const reqId = res.headers.get('x-request-id');

  if (res.status === 204) return undefined as T;

  const ct = res.headers.get('content-type') || '';
  const isJson = ct.includes('application/json');
  const payload = isJson ? await res.json().catch(() => null) : await res.text().catch(() => '');

  if (!res.ok) {
    const err = isJson && payload && typeof payload === 'object' && 'error' in payload
      ? (payload as { error: { code?: string; message?: string; details?: unknown } }).error
      : undefined;
    if (res.status === 401) {
      window.dispatchEvent(new CustomEvent('ictus:auth-expired'));
    }
    throw new ApiError(
      res.status,
      err?.code ?? `http_${res.status}`,
      err?.message ?? res.statusText ?? 'request_failed',
      err?.details,
      reqId,
    );
  }

  return payload as T;
}

export const apiGet  = <T>(path: string, opts?: ApiOptions) => api<T>(path, { ...opts, method: 'GET' });
export const apiPost = <T>(path: string, body?: unknown, opts?: ApiOptions) => api<T>(path, { ...opts, method: 'POST', body });
export const apiPatch = <T>(path: string, body?: unknown, opts?: ApiOptions) => api<T>(path, { ...opts, method: 'PATCH', body });
export const apiDelete = <T>(path: string, opts?: ApiOptions) => api<T>(path, { ...opts, method: 'DELETE' });
